import { useEffect } from "react";
import UIHero from "./components/UIHero";
import UIIntro from "./components/UIIntro";
import UIProjectsGrid from "./components/UIProjectsGrid";
import UIGallery from "./components/UIGallery";
import UITrust from "./components/UITrust";
import UICTA from "./components/UICTA";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import WhatsAppEnquiryForm from "@/components/feature/WhatsAppEnquiryForm";

export default function UrbanInfraProjectsPage() {
  useEffect(() => {
    document.title = "Urban Infra Projects | Villa Plots & Gated Community Layouts Karnataka | UrbanInfraProjects.in";

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        if (prop) el.setAttribute("property", name);
        else el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta(
      "description",
      "Explore 9+ eco-friendly villa plot projects and gated community layouts across Karnataka by Urban Infra. Premium residential plots with verified documentation and modern amenities. UrbanInfraProjects.in."
    );
    setMeta("keywords", "Urban Infra projects, villa plots Karnataka, gated community layouts, Amber Park, Anaaya Bliss, Sai Meadows, UrbanInfraProjects.in");
    setMeta("og:title", "Urban Infra Projects | Villa Plots & Gated Community Layouts Karnataka", true);
    setMeta("og:description", "9+ eco-friendly villa plot projects and gated community layouts across Karnataka. Premium plots with verified documentation by Urban Infra.", true);
    setMeta("og:url", `${import.meta.env.VITE_SITE_URL ?? "https://www.urbaninfraprojects.in"}/urbaninfra`, true);
    setMeta("og:site_name", "Urban Infra", true);
    setMeta("og:type", "website", true);

    // Schema.org JSON-LD — ItemList of real estate listings
    const schemaId = "schema-urbaninfra";
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = schemaId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://www.urbaninfraprojects.in";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Urban Infra Villa Plot Projects on Sarjapur Road",
      description:
        "List of premium villa plot projects and gated community layouts by Urban Infra on Sarjapur Road and Bagalur Road, Bengaluru.",
      url: `${siteUrl}/urbaninfra`,
      numberOfItems: 9,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Amber Park", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 2, name: "Anaaya Bliss", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 3, name: "Sai Meadows Phase 1", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 4, name: "Sai Meadows Phase 2", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 5, name: "Sai Meadows Phase 3", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 6, name: "Urban Meadows", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 7, name: "Urban Nest", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 8, name: "Urban Palms", url: `${siteUrl}/urbaninfra` },
        { "@type": "ListItem", position: 9, name: "Green Earth", url: `${siteUrl}/urbaninfra` },
      ],
    });

    window.scrollTo(0, 0);
    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SiteHeader />
      <main>
        <UIHero />
        <UIIntro />
        <UIProjectsGrid />
        <UIGallery />
        <UITrust />

        {/* WhatsApp Enquiry Section */}
        <section className="py-16 md:py-20 bg-[#EAF5E1]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="inline-flex items-center gap-2 bg-white text-[#7FBF3F] text-xs font-semibold px-4 py-2 rounded-full mb-5 border border-[#7FBF3F]/20">
                  <i className="ri-whatsapp-line"></i>
                  WhatsApp Enquiry
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1B5E20] mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Interested in Our Projects on Sarjapur Road?
                </h2>
                <p className="text-gray-600 text-base leading-relaxed mb-8">
                  Connect with us for full project details, pricing, and site visit support. Our team is available Monday to Saturday, 11 AM to 7 PM.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { icon: "ri-phone-line", label: "Call Us", value: "+91 9945124133", href: "tel:+919945124133" },
                    { icon: "ri-whatsapp-line", label: "WhatsApp", value: "+91 9945124133", href: "https://wa.me/919945124133" },
                    { icon: "ri-mail-line", label: "Email", value: "sales@urbaninfraproject.in", href: "mailto:sales@urbaninfraproject.in" },
                  ].map((c) => (
                    <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="nofollow noreferrer" className="flex items-center gap-4 bg-white rounded-xl p-4 hover:shadow-sm transition-all duration-300 cursor-pointer group border border-[#7FBF3F]/10">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#EAF5E1] rounded-lg flex-shrink-0">
                        <i className={`${c.icon} text-[#7FBF3F] text-base`}></i>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">{c.label}</p>
                        <p className="text-[#1B5E20] font-semibold text-sm">{c.value}</p>
                      </div>
                      <i className="ri-arrow-right-line text-[#7FBF3F] ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                    </a>
                  ))}
                </div>
                <div className="bg-white border border-[#7FBF3F]/10 rounded-xl p-5">
                  <p className="text-gray-500 text-xs mb-1">Office Address</p>
                  <p className="text-[#1B5E20] text-sm font-medium leading-relaxed">
                    #2, CNR Complex, First Floor,<br />
                    Muthanallur Cross, Sarjapur Main Road,<br />
                    Bangalore – 562125
                  </p>
                </div>
              </div>
              <WhatsAppEnquiryForm />
            </div>
          </div>
        </section>

        <UICTA />
      </main>
      <SiteFooter />
    </div>
  );
}
